/**
 * Space Color Utilities for FlexDisplay
 * Maps space state values to appropriate colors
 */

// Space state constants (matching Space.js)
export const SPACE_STATES = {
  VALUE_ABSENCE: 0,     // Available
  VALUE_PRESENCE: 1,    // Occupied
  VALUE_AVAIL: 2,       // Available
  VALUE_ALLOCATED: 3,   // Reserved
  VALUE_CHECKIN: 4,     // Checked In
  VALUE_CHECKOUT: 5,    // Checked Out
  VALUE_UNKNOWN: -1     // Unknown
};

// Color scheme for different states
export const STATE_COLORS = {
  // Available states - Green
  [SPACE_STATES.VALUE_ABSENCE]: '#28a745',   // Available (Green)
  [SPACE_STATES.VALUE_AVAIL]: '#28a745',     // Available (Green)
  [SPACE_STATES.VALUE_CHECKOUT]: '#28a745',  // Checked Out → Available (Green)
  
  // Occupied states - Red
  [SPACE_STATES.VALUE_PRESENCE]: '#dc3545',  // Occupied (Red)
  
  // Reserved states - Blue
  [SPACE_STATES.VALUE_ALLOCATED]: '#007bff', // Reserved (Blue)
  
  // Checked in states - Orange
  [SPACE_STATES.VALUE_CHECKIN]: '#fd7e14',   // Checked In (Orange)
  
  // Unknown states - Gray
  [SPACE_STATES.VALUE_UNKNOWN]: '#6c757d'    // Unknown (Gray)
};

// Alternative color schemes
export const COLOR_SCHEMES = {
  // Default scheme (current)
  default: STATE_COLORS,
  
  // High contrast scheme
  highContrast: {
    [SPACE_STATES.VALUE_ABSENCE]: '#00C851',     // Bright Green
    [SPACE_STATES.VALUE_AVAIL]: '#00C851',       // Bright Green
    [SPACE_STATES.VALUE_CHECKOUT]: '#00C851',    // Bright Green
    [SPACE_STATES.VALUE_PRESENCE]: '#FF4444',    // Bright Red
    [SPACE_STATES.VALUE_ALLOCATED]: '#2E7BFF',   // Bright Blue
    [SPACE_STATES.VALUE_CHECKIN]: '#FF8800',     // Bright Orange
    [SPACE_STATES.VALUE_UNKNOWN]: '#999999'      // Gray
  },
  
  // Colorblind friendly scheme
  colorblindFriendly: {
    [SPACE_STATES.VALUE_ABSENCE]: '#2E8B57',     // Sea Green
    [SPACE_STATES.VALUE_AVAIL]: '#2E8B57',       // Sea Green
    [SPACE_STATES.VALUE_CHECKOUT]: '#2E8B57',    // Sea Green
    [SPACE_STATES.VALUE_PRESENCE]: '#B22222',    // Fire Brick
    [SPACE_STATES.VALUE_ALLOCATED]: '#4169E1',   // Royal Blue
    [SPACE_STATES.VALUE_CHECKIN]: '#FF6347',     // Tomato
    [SPACE_STATES.VALUE_UNKNOWN]: '#708090'      // Slate Gray
  }
};

/**
 * Get color for a space based on its availability state
 * @param {Object} space - Space object with senses
 * @param {string} scheme - Color scheme to use ('default', 'highContrast', 'colorblindFriendly')
 * @returns {string} - Hex color code
 */
export function getSpaceColor(space, scheme = 'default') {
  if (!space || !space.senses || !space.senses.availability) {
    return COLOR_SCHEMES[scheme][SPACE_STATES.VALUE_UNKNOWN];
  }

  const availabilityState = space.senses.availability.state;
  const colorScheme = COLOR_SCHEMES[scheme] || COLOR_SCHEMES.default;
  
  // Return color for the state, fallback to unknown if state not found
  return colorScheme[availabilityState] || colorScheme[SPACE_STATES.VALUE_UNKNOWN];
}

/**
 * Get color for a space with fallback logic
 * Checks multiple sensor types in priority order
 * @param {Object} space - Space object with senses
 * @param {string} scheme - Color scheme to use
 * @returns {string} - Hex color code
 */
export function getSpaceColorWithFallback(space, scheme = 'default') {
  if (!space || !space.senses) {
    return COLOR_SCHEMES[scheme][SPACE_STATES.VALUE_UNKNOWN];
  }

  const colorScheme = COLOR_SCHEMES[scheme] || COLOR_SCHEMES.default;

  // Priority order: availability > spot > device > flo
  const sensors = ['availability', 'spot', 'device', 'flo'];
  
  for (const sensor of sensors) {
    if (space.senses[sensor] && typeof space.senses[sensor].state !== 'undefined') {
      const state = space.senses[sensor].state;
      if (colorScheme[state]) {
        return colorScheme[state];
      }
    }
  }

  // Fallback to unknown
  return colorScheme[SPACE_STATES.VALUE_UNKNOWN];
}

/**
 * Get human-readable state name
 * @param {number} state - State value
 * @returns {string} - Human-readable state name
 */
export function getStateName(state) {
  const stateNames = {
    [SPACE_STATES.VALUE_ABSENCE]: 'Available',
    [SPACE_STATES.VALUE_PRESENCE]: 'Occupied',
    [SPACE_STATES.VALUE_AVAIL]: 'Available',
    [SPACE_STATES.VALUE_ALLOCATED]: 'Reserved',
    [SPACE_STATES.VALUE_CHECKIN]: 'Checked In',
    [SPACE_STATES.VALUE_CHECKOUT]: 'Available',
    [SPACE_STATES.VALUE_UNKNOWN]: 'Unknown'
  };
  
  return stateNames[state] || 'Unknown';
}

/**
 * Get space status info including color and name
 * @param {Object} space - Space object
 * @param {string} scheme - Color scheme to use
 * @returns {Object} - {color, state, name, sensor}
 */
export function getSpaceStatus(space, scheme = 'default') {
  if (!space || !space.senses) {
    return {
      color: COLOR_SCHEMES[scheme][SPACE_STATES.VALUE_UNKNOWN],
      state: SPACE_STATES.VALUE_UNKNOWN,
      name: 'Unknown',
      sensor: 'none'
    };
  }

  const colorScheme = COLOR_SCHEMES[scheme] || COLOR_SCHEMES.default;
  const sensors = ['availability', 'spot', 'device', 'flo'];
  
  for (const sensor of sensors) {
    if (space.senses[sensor] && typeof space.senses[sensor].state !== 'undefined') {
      const state = space.senses[sensor].state;
      return {
        color: colorScheme[state] || colorScheme[SPACE_STATES.VALUE_UNKNOWN],
        state: state,
        name: getStateName(state),
        sensor: sensor
      };
    }
  }

  // Fallback
  return {
    color: colorScheme[SPACE_STATES.VALUE_UNKNOWN],
    state: SPACE_STATES.VALUE_UNKNOWN,
    name: 'Unknown',
    sensor: 'none'
  };
}

/**
 * Check if space is available (any available state)
 * @param {Object} space - Space object
 * @returns {boolean} - True if space is available
 */
export function isSpaceAvailable(space) {
  if (!space || !space.senses || !space.senses.availability) {
    return false;
  }

  const state = space.senses.availability.state;
  return state === SPACE_STATES.VALUE_ABSENCE || 
         state === SPACE_STATES.VALUE_AVAIL || 
         state === SPACE_STATES.VALUE_CHECKOUT;
}

/**
 * Check if space is occupied
 * @param {Object} space - Space object
 * @returns {boolean} - True if space is occupied
 */
export function isSpaceOccupied(space) {
  if (!space || !space.senses || !space.senses.availability) {
    return false;
  }

  return space.senses.availability.state === SPACE_STATES.VALUE_PRESENCE;
}

/**
 * Check if space is reserved
 * @param {Object} space - Space object
 * @returns {boolean} - True if space is reserved
 */
export function isSpaceReserved(space) {
  if (!space || !space.senses || !space.senses.availability) {
    return false;
  }

  return space.senses.availability.state === SPACE_STATES.VALUE_ALLOCATED;
}

export default {
  SPACE_STATES,
  STATE_COLORS,
  COLOR_SCHEMES,
  getSpaceColor,
  getSpaceColorWithFallback,
  getStateName,
  getSpaceStatus,
  isSpaceAvailable,
  isSpaceOccupied,
  isSpaceReserved
};
